import { useState } from "react";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../api/useFetchObjects";
import useAdd from "../../../api/useAdd";
import useUpdate from "../../../api/useUpdate";
import useDelete from "../../../api/useDelete";
import AssignmentFormDialog from "./AssignmentFormDialog";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";

export default function AssignmentList() {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [seminarFilter, setSeminarFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);

  const { data: seminarsData } = useFetchObjects(["seminars-list"], "seminars/?page_size=500&ordering=-seminar_date", token);
  const seminars = seminarsData?.results ?? seminarsData ?? [];

  const assignUrl = seminarFilter
    ? `assignments/?page=${page}&page_size=${rowsPerPage}&seminar=${seminarFilter}&ordering=-due_date`
    : `assignments/?page=${page}&page_size=${rowsPerPage}&ordering=-due_date`;
  const { data, isLoading, isError, error } = useFetchObjects(
    ["assignments", page, rowsPerPage, seminarFilter],
    assignUrl,
    token
  );

  const { handleAdd } = useAdd("assignments", token, t("academic.assignmentAdded", "Assignment added."));
  const { handleUpdate } = useUpdate("assignments", token);
  const { handleDelete, ConfirmDialog } = useDelete("assignments", token);

  const rows = Array.isArray(data) ? data : data?.results || [];
  const totalCount = Array.isArray(data) ? data.length : data?.count || 0;

  const handleOpenAdd = () => { setEditingAssignment(null); setDialogOpen(true); };
  const handleOpenEdit = (row) => { setEditingAssignment(row); setDialogOpen(true); };
  const handleCloseDialog = () => { setDialogOpen(false); setEditingAssignment(null); };
  const handleSave = (payload) => {
    if (editingAssignment) handleUpdate(editingAssignment.id, payload);
    else handleAdd(payload);
    handleCloseDialog();
  };

  if (isError) return <Alert severity="error" sx={{ m: 2 }}>{error?.message || "Failed to load assignments"}</Alert>;

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ConfirmDialog />
      <Paper sx={{ p: 3, width: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>{t("academic.assignments", "Assignments")}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{t("academic.filterBySeminar", "Filter by seminar")}</InputLabel>
              <Select value={seminarFilter} label={t("academic.filterBySeminar", "Filter by seminar")} onChange={(e) => { setSeminarFilter(e.target.value); setPage(1); }}>
                <MenuItem value="">{t("common.all", "All")}</MenuItem>
                {seminars.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>{t("academic.addAssignment", "Add assignment")}</Button>
          </Box>
        </Box>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <TableContainer sx={{ width: "100%", overflow: "auto" }}>
              <Table size="small" sx={{ minWidth: 500 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.200" }}>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>{t("academic.assignmentTitle", "Title")}</strong></TableCell>
                    <TableCell><strong>{t("academic.seminar", "Seminar")}</strong></TableCell>
                    <TableCell><strong>{t("academic.dueDate", "Due date")}</strong></TableCell>
                    <TableCell align="right"><strong>{t("common.actions", "Actions")}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>{t("common.noRecords", "No records found.")}</TableCell></TableRow>
                  ) : (
                    rows.map((row, idx) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{(page - 1) * rowsPerPage + idx + 1}</TableCell>
                        <TableCell>{row.title}</TableCell>
                        <TableCell>{row.seminar_display || "—"}</TableCell>
                        <TableCell>{row.due_date || "—"}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenEdit(row)} title={t("common.edit", "Edit")}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => handleDelete(row.id)} color="error" title={t("common.delete", "Delete")}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={totalCount} page={page - 1} onPageChange={(e, p) => setPage(p + 1)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(1); }} rowsPerPageOptions={[10, 25, 50]} />
          </>
        )}
      </Paper>
      <AssignmentFormDialog open={dialogOpen} onClose={handleCloseDialog} assignment={editingAssignment} seminars={seminars} onSave={handleSave} />
    </Box>
  );
}
